import Vue from 'vue'

import { get } from 'lodash'

import { Colors } from '@sigma-frontend-libs/commons'

import { buttonModes, components, generateUniqueKey, icons, inputModes, states } from '@/utils'

import button from '@/components/button'
import buttonClone from '@/components/button/clone'
import buttonConfirmRemove from '@/components/button/confirm/remove'
import buttonCopy from '@/components/button/copy'
import buttonFavorite from '@/components/button/favorite'
import buttonRefresh from '@/components/button/refresh'
import entityInfo from '@/components/entity/info'
import info from '@/components/info'
import componentNotFound from '@/components/misc/componentNotFound'
import pagination from '@/components/pagination'
import preloader from '@/components/preloader'
import sourceBox from '@/components/source/box'
import textOverflow from '@/components/text/overflow'

function renderTab(h, value) {
  return h(
    button,
    {
      props: {
        label: this.getTranslate(`commons.tabs.${value}`),
        mode: this.currentTab === value ? buttonModes.flat : buttonModes.clear,
        color: this.currentTab === value ? Colors.primary : undefined
      },
      on: {
        click: () => {
          this.currentTab = value
        },
        dblclick: event => {
          event.preventDefault()
          this.setDefaultTab(value)
        }
      }
    }
  )
}
function renderTabs(h, options) {
  if (!this.$route.params.id) {
    if (options.has.tabs) {
      return h(
        'div',
        {
          class: 'service-template-tabs'
        },
        [ 'all', 'visited', 'favorites' ].map(type => {
          return renderTab.call(this, h, type, options)
        })
      )
    }
  }
}

function renderRemoveButton(h, options) {
  if (options.remove !== false) {
    const loading = this.restData[options.serviceName].remove.state === states.loading

    return h(
      buttonConfirmRemove,
      {
        props: {
          labeled: false,
          loading,
          disabled: !this.canRemove || loading,
          callback: () => this.remove()
        }
      }
    )
  }
}

function renderFavoriteButton(h, options) {
  if (options.has.tabs) {
    return h(
      buttonFavorite,
      {
        props: {
          id: get(this.restData[options.serviceName], 'get.data.id'),
          service: options.serviceName
        }
      }
    )
  }
}

function renderSupportButton(h, options) {
  if (options.has.support) {
    return h(
      button,
      {
        props: {
          label: this.getTranslate('misc.buttons.support'),
          color: Colors.primary,
          mode: buttonModes.flat
        },
        on: {
          click: () => {
            Vue.router.push({ name: 'support' })
          }
        }
      }
    )
  }
}

function renderCreateDialog(h, options) {
  if (options.create !== false) {
    return h(
      components.dialog,
      {
        props: {
          title: this.getTranslate(`${options.serviceNameViaPoint}.titles.create`),
          value: this.showCreateDialog,
          rounded: true,
          maxWidth: options.width,

          ...options.dialog.create
        },
        on: {
          input: event => {
            this.showCreateDialog = event
          }
        }
      },
      [
        renderForm.call(this, h, options, this.restData[options.serviceName].create.data, 'create'),

        h(
          'div',
          {
            class: 'dialog__footer',
            slot: 'footer'
          },
          [
            renderSupportButton.call(this, h, options),

            h('div', { class: 'ff' }),

            h(
              button,
              {
                props: {
                  label: this.getTranslate('misc.buttons.cancel'),
                  mode: buttonModes.flat,
                  disabled: this.restData[options.serviceName].create.state === states.loading
                },
                on: {
                  click: () => {
                    this.showCreateDialog = false
                  }
                }
              }
            ),

            h(
              button,
              {
                props: {
                  label: this.getTranslate('misc.buttons.create'),
                  color: Colors.primary,
                  loading: this.restData[options.serviceName].create.state === states.loading,
                  disabled: this.restData[options.serviceName].create.state === states.loading || !this.restData[options.serviceName].create.isValid || !this.canCreate
                },
                on: {
                  click: () => {
                    this.create()
                  }
                }
              }
            )
          ]
        )
      ]
    )
  }
}
function renderAfterCreateDialog(h, options) {
  try {
    return h(
      require(`@/components/services/${options.serviceName}/dialog/after/create`).default,
      {
        props: {
          value: this.show.after.create,
          data: this.createdData
        },
        on: {
          input: event => {
            this.show.after.create = event
          }
        }
      }
    )
  } catch (error) {}
}

function renderCreateButton(h, options) {
  if (options.create !== false) {
    return h(
      button,
      {
        props: {
          icon: icons.add,
          mode: buttonModes.flat,
          color: Colors.secondary,
          disabled: !this.canCreate
        },
        on: {
          click: () => {
            this.showCreateDialog = true
          }
        },
        scopedSlots: {
          dialog: () => [
            renderCreateDialog.call(this, h, options),
            renderAfterCreateDialog.call(this, h, options)
          ]
        }
      }
    )
  }
}

function renderSourceDialog(h, options) {
  if (this.showSourceDialog && options.has.source) {
    return h(
      components.dialog,
      {
        props: {
          title: this.getTranslate('misc.sourceData'),
          value: this.showSourceDialog,
          rounded: this.viewport.breakpoint.mdUp,
          maxWidth: this.viewport.breakpoint.mdUp ? 600 : '100%'
        },
        on: {
          input: event => {
            this.showSourceDialog = event
          }
        }
      },
      [
        h(
          'div',
          {
            class: 'px-2'
          },
          [
            h(
              sourceBox,
              {
                props: {
                  value: this.restData[options.serviceName].get.data,
                  rounded: false
                }
              }
            )
          ]
        ),

        h(
          'div',
          {
            class: 'pa-2 fjcfe',
            slot: 'footer'
          },
          [
            h(
              button,
              {
                props: {
                  label: this.getTranslate('misc.buttons.close'),
                  mode: buttonModes.flat,
                  disabled: this.restData[options.serviceName].create.state === states.loading
                },
                on: {
                  click: () => {
                    this.showSourceDialog = !this.showSourceDialog
                  }
                }
              }
            )
          ]
        )
      ]
    )
  }
}
function renderSourceButton(h, options) {
  if (options.has.source) {
    return h(
      button,
      {
        props: {
          icon: icons.code,
          mode: buttonModes.flat,
          color: Colors.primary
        },
        on: {
          click: () => {
            this.showSourceDialog = true
          }
        },
        scopedSlots: {
          dialog: () => renderSourceDialog.call(this, h, options)
        }
      }
    )
  }
}

function renderCloneButton(h, options) {
  try {
    return h(
      require(`@/components/services/${options.serviceName}/after/title/clone`).default,
      {
        props: {
          value: this.restData[options.serviceName].get.data
        }
      }
    )
  } catch (error) {
    if (options.has.clone) {
      return h(
        buttonClone,
        {
          props: {
            service: options.serviceName,
            value: this.restData[options.serviceName].get.data,
            clone: () => this.clone()
          }
        }
      )
    }
  }
}
function renderUpdateButton(h, options, label) {
  if (options.update !== false) {
    return h(
      button,
      {
        props: {
          icon: icons.save,
          mode: !label ? buttonModes.flat : undefined,
          color: Colors.primary,
          label: label ? this.getTranslate('misc.buttons.save') : undefined,
          tooltip: label ? undefined : this.getTranslate('misc.buttons.save'),
          loading: this.updateLoading,
          disabled: this.updateDisabled
        },
        on: {
          click: () => {
            this.update()
          }
        }
      }
    )
  }
}

function renderPagination(h, options, trigger = true) {
  if (trigger) {
    if (!this.$route.params.id) {
      if (this.currentTab === undefined || this.currentTab === 'all') {
        const limit = this.restData[options.serviceName].find.pagination.limit

        return h(
          'div',
          {
            class: 'service-template-pagination'
          },
          [
            h(
              pagination,
              {
                props: {
                  value: this.restData[options.serviceName].find.pagination,
                  rowsPerPage: [ limit, limit * 2, limit * 4 ],
                  service: options.serviceName
                },
                on: {
                  input: event => {
                    this.restData[options.serviceName].find.pagination = event
                  }
                }
              }
            ),

            h(
              buttonRefresh,
              {
                props: {
                  state: this.restData[options.serviceName].find.state,
                  shortClickMethod: () => this.rest[options.serviceName].find(),
                  longClickMethod: () => this.rest[options.serviceName].find({}, { noCache: true })
                }
              }
            )
          ]
        )
      }
    }
  }
}

function renderSearch(h, options) {
  if (options.has.search) {
    return h(
      components['text-field'],
      {
        props: {
          value: this.restData[options.serviceName].find.filter.$search,
          label: this.getTranslate(`${options.serviceNameViaPoint}.labels.search`),
          suffix: this.restData[options.serviceName].find.pagination.total,
          mode: inputModes.outline,
          afterIcon: icons.search,
          afterIconCallback: () => this.rest[options.serviceName].find(),
          rounded: true,
          dense: true,
          details: false,
          clearable: true
        },
        on: {
          input: event => {
            this.restData[options.serviceName].find.filter.$search = event
          }
        }
      }
    )
  }
}
function renderFilter(h, options) {
  try {
    return h(
      require(`@/components/services/${options.serviceName}/filter`).default,
      {
        props: {
          value: this.restData[options.serviceName].find.filter
        },
        on: {
          input: event => {
            this.restData[options.serviceName].find.filter = event
          }
        }
      }
    )
  } catch (error) {
    return
  }
}
function renderHeaderFields(h, options) {
  if (options.has.search || options.has.filter) {
    return h(
      'div',
      {
        class: 'service-template-header__fields',
        style: { gridTemplateColumns: this.headerFieldsGridTemplateColumns }
      },
      [
        renderSearch.call(this, h, options),
        renderFilter.call(this, h, options)
      ]
    )
  }
}
function renderHeaderActions(h, options) {
  if (options.create !== false) {
    return h(
      'div',
      {
        class: 'service-template-header__actions'
      },
      [ renderCreateButton.call(this, h, options) ]
    )
  }
}
function renderHeaderContent(h, options) {
  if (options.has.search || options.has.filter || options.create !== false) {
    return h(
      'div',
      {
        class: 'service-template-header__content'
      },
      [
        renderHeaderFields.call(this, h, options),
        renderHeaderActions.call(this, h, options)
      ]
    )
  }
}
function renderHeader(h, options) {
  if (!this.$route.params.id) {
    return h(
      'div',
      {
        class: 'service-template-header'
      },
      [
        renderHeaderContent.call(this, h, options),
        renderPagination.call(this, h, options)
      ]
    )
  }
}

function renderBackButton(h) {
  return h(
    button,
    {
      props: {
        icon: icons.keyboard_arrow_left,
        mode: buttonModes.flat
      },
      on: {
        click: () => {
          this.$router.go(-1)
        }
      }
    }
  )
}

function renderCopyLinkForSlackButton(h, { serviceNameViaPoint }) {
  if (this.checkPermissions(`advanced.${serviceNameViaPoint}.get`)) {
    return h(
      buttonCopy,
      {
        props: {
          value: this.titleURLSlack,
          iconSVG: 'slack',
          tooltip: this.getTranslate('commons.tooltips.copyLinkForSlack')
        }
      }
    )
  }
}

function renderAfterTitleComponent(h, options) {
  try {
    return h(
      require(`@/components/services/${options.serviceName}/after/title`).default,
      {
        props: {
          value: this.restData[options.serviceName].get.data,
          get: () => this.rest[options.serviceName].get(this.restData[options.serviceName].get.data.id)
        }
      }
    )
  } catch (error) {
    return
  }
}

function renderAfterTitle(h, options) {
  return h(
    'div',
    {
      class: 'faic grid-gap--8'
    },
    [
      renderAfterTitleComponent.call(this, h, options),
      renderCloneButton.call(this, h, options),
      renderCopyLinkForSlackButton.call(this, h, options)
    ]
  )
}

function renderTitle(h, options) {
  return h(
    textOverflow,
    {
      class: {
        'body-1': true,
        'link link--passive': options.has.copy.title && this.$title,
        'text--grey': !this.$title
      },
      props: {
        value: this.title
      },
      on: {
        click: () => {
          if (this.$title) {
            this.copy('title', this.$title)
          }
        }
      }
    }
  )
}
function renderId(h, options) {
  const id = this.restData[options.serviceName].get.data.id
  if (id) {
    return h(
      textOverflow,
      {
        class: {
          'small text--grey': true,
          'link link--passive': options.has.copy.id
        },
        props: {
          value: id
        },
        on: {
          click: () => this.copy('id', id)
        }
      }
    )
  }
}
function renderTitleBlock(h, options) {
  if (this.restData[options.serviceName].get.state === states.ready) {
    if (this.restData[options.serviceName].get.data) {
      return h(
        'div',
        {
          class: 'grid grid-gap--8',
          style: { 'grid-template-columns': '1fr auto' }
        },
        [
          h(
            'div',
            {
              class: 'grid grid-gap--0 faic'
            },
            [
              renderTitle.call(this, h, options),
              renderId.call(this, h, options)
            ]
          ),

          h(
            'div',
            {
              class: 'faic fjcfe'
            },
            [ renderAfterTitle.call(this, h, options) ]
          )
        ]
      )
    }
  } else if (this.restData[options.serviceName].get.state === states.loading) {
    return h(
      'div',
      {
        class: 'preloader__holder'
      },
      [ h(preloader) ]
    )
  }
}

function renderAsteriskInfo(h) {
  return h(
    'div',
    {
      class: 'pa-2'
    },
    [
      h(
        info,
        {
          props: {
            value: this.getTranslate('commons.hints.form.asterisk'),
            color: Colors.grey
          }
        }
      )
    ]
  )
}
function renderEntityInfo(h, options) {
  return h(
    entityInfo,
    {
      class: 'my-2',
      props: { value: this.restData[options.serviceName].get.data }
    }
  )
}

function renderFormHeader(h, options) {
  return h(
    'div',
    {
      class: 'service-template-form__header'
    },
    [
      renderBackButton.call(this, h, options),
      renderTitleBlock.call(this, h, options)
    ]
  )
}
function renderForm(h, options, data, method) {
  try {
    return h(
      require(`@/components/services/${options.serviceName}/form`).default,
      {
        class: options.formClass ?? 'pa-3',
        props: {
          value: data,
          isNew: method === 'create',
          loading: !!this.restData[options.serviceName][method]
          && this.restData[options.serviceName][method].state === states.loading,
          updatedData: this.updatedData
        },
        on: {
          input: event => {
            data = event
          },
          submit: () => {
            if (typeof this[method] === 'function') {
              this[method]()
            }
          },
          validation: event => {
            if (
              this.restData &&
              this.restData[options.serviceName] &&
              typeof this.restData[options.serviceName][method] !== 'undefined'
            ) {
              this.restData[options.serviceName][method].isValid = event
            }
          }
        }
      }
    )
  } catch (error) {
    return h(componentNotFound)
  }
}
function renderFormAfter(h, options) {
  return [
    h(components.divider),
    renderAsteriskInfo.call(this, h),
    h(components.divider),
    renderEntityInfo.call(this, h, options)
  ]
}
function renderFormContent(h, options) {
  let { accent, accentColor, accentColorName, accentColorNameSelector, accentSize, accentPosition } = options.form || {}
  accentColorNameSelector ? accentColorName = accentColorNameSelector(this.restData[options.serviceName].get.data) : accentColorName

  return h(
    components.card,
    {
      props: {
        outline: true,
        rounded: true,
        maxWidth: options.width,
        width: '100%',
        accent,
        accentColor,
        accentColorName,
        accentSize,
        accentPosition
      }
    },
    [
      renderForm.call(this, h, options, this.restData[options.serviceName].get.data, 'update'),
      renderFormAfter.call(this, h, options)
    ]
  )
}
function renderFormButtonRefresh(h, options) {
  return h(
    buttonRefresh,
    {
      props: {
        state: this.restData[options.serviceName].get.state,
        shortClickMethod: () => this.rest[options.serviceName].get(this.restData[options.serviceName].get.data.id),
        longClickMethod: () => this.rest[options.serviceName].get(this.restData[options.serviceName].get.data.id, { noCache: true })
      }
    }
  )
}
function renderFooterActions(h, options) {
  try {
    return h(
      require(`@/components/services/${options.serviceName}/footer/actions`).default,
      {
        props: {
          value: this.restData[options.serviceName].get.data,
          get: () => this.rest[options.serviceName].get(this.restData[options.serviceName].get.data.id)
        }
      }
    )
  } catch (error) {
    return
  }
}
function renderFormFooter(h, options) {
  return h(
    'div',
    {
      class: 'service-template-form__footer'
    },
    [
      h(
        'div',
        {
          class: 'service-template-form__footer-side'
        },
        [
          renderRemoveButton.call(this, h, options),
          renderFavoriteButton.call(this, h, options)
        ]
      ),

      h(
        'div',
        {
          class: 'service-template-form__footer-side'
        },
        [
          renderFooterActions.call(this, h, options),
          renderSourceButton.call(this, h, options),
          renderFormButtonRefresh.call(this, h, options),
          renderUpdateButton.call(this, h, options, true)
        ]
      )
    ]
  )
}

function renderFirstCol(h, options) {
  return h(
    'div',
    {
      class: 'service-template-cols-first',
      style: {
        'max-width': `${options.width}px`,
        width: '100%'
      }
    },
    [
      renderFormHeader.call(this, h, options),
      renderFormContent.call(this, h, options),
      renderFormFooter.call(this, h, options)
    ]
  )
}
function renderSecondCol(h, options, data) {
  try {
    return h(
      require('@/components/templates/service/cols/second').default,
      {
        props: {
          value: data,
          service: options.serviceName,
          id: this.$route.params.id
        }
      }
    )
  } catch (error) {
    return h('div')
  }
}
function renderThirdCol(h, options) {
  try {
    return h(require(`@/components/services/${options.serviceName}/cols/third`).default)
  } catch (error) {
    return h('div')
  }
}

function renderAbsolutePreloader(h, options) {
  if (this.restData[options.serviceName].find.state === states.loading) {
    return h(
      'div',
      {
        class: 'preloader__holder preloader__holder--absolute'
      },
      [ h(preloader) ]
    )
  }
}
function renderPreloader(h) {
  return h(
    'div',
    {
      class: 'preloader__holder pa-5'
    },
    [ h(preloader) ]
  )
}
function renderItem(h, options, item) {
  return h(
    require('@/components/templates/service/item').default,
    {
      props: {
        value: item,
        service: options.serviceName
      },
      key: item.id || generateUniqueKey() || Date.now()
    }
  )
}
function renderItems(h, options, items) {
  return h(
    'div',
    {
      class: 'service-template-items'
    },
    [
      items.map(item => {
        return renderItem.call(this, h, options, item)
      }),

      renderAbsolutePreloader.call(this, h, options)
    ]
  )
}
function renderCols(h, options) {
  let cols = 1
  if (typeof renderThirdCol.call(this, h, options) === 'object') {
    cols = 2
  }
  const data = this.restData[options.serviceName].get.data
  if (data) {
    return h(
      'div',
      {
        class: 'grid faifs',
        style: {
          'grid-template-columns': this.viewport.breakpoint.mdUp ? `${options.width}px repeat(${cols}, minmax(400px, 600px))` : undefined
        }
      },
      [
        renderFirstCol.call(this, h, options),
        renderSecondCol.call(this, h, options, data),
        renderThirdCol.call(this, h, options)
      ]
    )
  } else {
    return h(components.empty)
  }
}
function renderBodyByTab(h, options, items, trigger) {
  if (trigger) {
    return renderItems.call(this, h, options, items)
  } else {
    switch (this.currentTab) {
      case 'visited':
      case 'favorites': {
        if (this.loading[this.currentTab]) {
          return renderPreloader.call(this, h)
        }

        return h(components.empty)
      }
      case 'all':
      default: {
        switch (this.restData[options.serviceName].find.state) {
          case states.loading: return renderPreloader.call(this, h)
          case states.ready:
          default: return h(components.empty)
        }
      }
    }
  }
}
function renderBody(h, options) {
  if (this.$route.params.id) {
    switch (this.restData[options.serviceName].get.state) {
      case states.ready: return renderCols.call(this, h, options)
      case states.loading: return renderPreloader.call(this, h)
      case states.error: return h(components.empty)
    }
  } else {
    const { data, pagination } = this.restData[options.serviceName].find
    switch (this.currentTab) {
      case 'visited':
      case 'favorites': return renderBodyByTab.call(this, h, options, this[this.currentTab], this[this.currentTab].length > 0)
      case 'all':
      default: return renderBodyByTab.call(this, h, options, data, pagination.total > 0)
    }
  }
}

export default function(h, options) {
  const { total, limit } = this.restData[options.serviceName].find.pagination

  return h(
    'div',
    {
      class: 'service-template'
    },
    [
      renderTabs.call(this, h, options),
      renderHeader.call(this, h, options),
      renderBody.call(this, h, options),
      renderPagination.call(this, h, options, total > limit)
    ]
  )
}
